import { Upload, Sparkles, AlertCircle } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import type { Ref } from 'react';
import { cn } from '@/utils';

// ---------------------------------------------------------------------------
// Shared upload validation — used by this drop zone, the header import button
// and the home page's conversion pipeline, so every entry point agrees on what
// is accepted. The vectorize API takes png/jpeg/webp/avif; gif and bmp are
// accepted here and re-encoded to PNG in the browser before upload.
// ---------------------------------------------------------------------------

export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/avif',
  'image/gif',
  'image/bmp',
] as const;

/** Value for an <input type="file" accept>. */
export const ACCEPTED_IMAGE_ACCEPT = ACCEPTED_IMAGE_TYPES.join(',');

/** Raw file ceiling. Anything that passes is downscaled to fit the upload body limit. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export const UPLOAD_HINT = 'PNG, JPG, WebP, AVIF, GIF or BMP, up to 20 MB';

const EXT_TO_TYPE: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
  bmp: 'image/bmp',
};

export type UploadValidation =
  | { ok: true }
  | { ok: false; reason: 'type' | 'size' | 'empty'; message: string };

function formatMb(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateImageFile(file: File): UploadValidation {
  // Some platforms report an empty MIME type (e.g. AVIF on older macOS) — fall back to the extension.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const type = file.type || EXT_TO_TYPE[ext] || '';
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type)) {
    const label = ext && ext !== file.name.toLowerCase() ? `.${ext}` : file.type || 'this file type';
    return {
      ok: false,
      reason: 'type',
      message: `${label} isn't supported. Use a PNG, JPG, WebP, AVIF, GIF or BMP image.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, reason: 'empty', message: 'That file is empty. Choose another image.' };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      reason: 'size',
      message: `That file is ${formatMb(file.size)}. The limit is ${formatMb(MAX_UPLOAD_BYTES)}.`,
    };
  }
  return { ok: true };
}

interface UploadZoneProps {
  onUpload: (file: File) => void;
  isProcessing: boolean;
  /** Stage text announced while processing (e.g. "Analyzing image..."). */
  stage?: string;
  /** Error raised outside the zone (e.g. the file could not be read). */
  error?: string | null;
  /** Lets the page open the picker programmatically (e.g. a CTA lower down). */
  inputRef?: Ref<HTMLInputElement>;
  /** Line under the heading; the converter landing pages pass one about their format. */
  description?: string;
}

// Left tile icon: a picture glyph built from square pixels, i.e. the raster you
// are about to upload. Accent colors come from the logo letters.

// 12x12 grid of 2-unit cells on a 24-unit canvas.
// x = frame, s = sun, m = front hill, n = back hill.
const PIXEL_GLYPH = [
  '.xxxxxxxxxx.',
  'x..........x',
  'x..........x',
  'x......ss..x',
  'x......ss..x',
  'x..........x',
  'x....m.....x',
  'x...mmm..n.x',
  'x..mmmmmnnnx',
  'x.mmmmmmmnnx',
  'xmmmmmmmmmmx',
  '.xxxxxxxxxx.',
];
const PIXEL_FILL: Record<string, string> = { x: 'currentColor', s: '#F77261', m: '#50B886', n: '#4887DF' };

function PixelIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} shapeRendering="crispEdges" aria-hidden="true">
      {PIXEL_GLYPH.flatMap((row, y) =>
        row.split('').map((c, x) =>
          // 2.2 wide so neighbouring cells overlap and no hairline seams show once the tile is rotated.
          c === '.' ? null : <rect key={`${x}-${y}`} x={x * 2} y={y * 2} width={2.2} height={2.2} fill={PIXEL_FILL[c]} />
        )
      )}
    </svg>
  );
}

export function UploadZone({ onUpload, isProcessing, stage, error, inputRef, description }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const hintId = useId();
  const errorId = useId();

  // A fresh external error replaces any stale local one.
  useEffect(() => {
    if (error) setLocalError(null);
  }, [error]);

  const shownError = localError ?? error ?? null;

  const accept = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      const result = validateImageFile(file);
      if (!result.ok) {
        setLocalError(result.message);
        return;
      }
      setLocalError(null);
      onUpload(file);
    },
    [onUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      accept(e.dataTransfer.files[0]);
    },
    [accept]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      accept(e.target.files?.[0]);
      // Reset so picking the same file again still fires a change event.
      e.target.value = '';
    },
    [accept]
  );

  if (isProcessing) {
    return (
      <div
        aria-busy="true"
        className="flex flex-col items-center justify-center py-20 space-y-6 text-center rounded-3xl border border-cream-200 bg-white"
      >
        <div className="w-14 h-14 rounded-xl bg-cream-100 border border-cream-200 flex items-center justify-center text-ink-soft animate-pulse motion-reduce:animate-none">
          <Sparkles className="w-6 h-6" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-medium tracking-tight text-ink">Vectorizing…</h3>
          <p role="status" aria-live="polite" className="text-[13px] text-ink-muted max-w-sm">
            {stage || 'Preparing image…'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'group relative w-full rounded-3xl border border-dashed transition-all duration-200 ease-out',
          'px-10 py-16 sm:px-14 sm:py-20',
          'focus-within:ring-2 focus-within:ring-sky focus-within:ring-offset-2 focus-within:ring-offset-cream-50',
          isDragging
            ? 'border-sky bg-sky-bg/40'
            : shownError
              ? 'border-clay bg-white'
              : 'border-neutral-800 bg-white hover:border-black hover:shadow-md hover:shadow-black/5'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_ACCEPT}
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          aria-label="Upload image"
          aria-describedby={shownError ? `${hintId} ${errorId}` : hintId}
          aria-invalid={shownError ? true : undefined}
          title=""
        />

        <div className="flex flex-col items-center text-center gap-5 sm:gap-6 pointer-events-none">
          {/* Two rotated icon tiles: pixel picture + upload */}
          <div className={cn('flex -space-x-4 transition-transform duration-200', isDragging && 'scale-105')}>
            <div className={cn(
              'w-16 h-16 rounded-2xl flex items-center justify-center rotate-[-8deg] shadow-sm transition-all duration-200 border',
              'group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:rotate-[-10deg]',
              isDragging
                ? 'bg-sky-bg border-sky/30'
                : 'bg-cream-100 border-cream-200'
            )}>
              <PixelIcon className={cn(
                'w-9 h-9 -translate-x-[3px] transition-colors duration-200',
                isDragging ? 'text-sky' : 'text-ink-faint'
              )} />
            </div>
            <div className={cn(
              'w-16 h-16 rounded-2xl flex items-center justify-center rotate-[8deg] shadow-sm z-10 transition-all duration-200 border border-white/20',
              'group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:rotate-[10deg]',
              'bg-gradient-to-br from-[#5F9CE8] to-[#4887DF]'
            )}>
              <Upload className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
          </div>

          <div className="space-y-3 max-w-md">
            <h2 className={cn(
              'text-2xl sm:text-[26px] font-semibold tracking-tight transition-colors duration-200',
              isDragging ? 'text-sky' : 'text-ink'
            )}>
              {isDragging ? 'Release to vectorize' : 'Drop your image here'}
            </h2>
            <p className="text-ink-muted text-[14px] leading-relaxed">
              {description ?? 'Convert a raster image into a crisp, scalable, editable vector graphic.'}
            </p>
            <p id={hintId} className="text-[12px] text-ink-muted">
              {UPLOAD_HINT}
            </p>
          </div>

          {/* Pill CTA */}
          <div className={cn(
            'inline-flex items-center justify-center px-5 py-2.5 text-[13px] font-medium rounded-full border transition-all duration-200 group-hover:translate-y-0.5',
            isDragging
              ? 'bg-sky text-white border-sky'
              : 'bg-cream-100 text-ink border-cream-300'
          )}>
            Browse files
          </div>
        </div>
      </div>

      {shownError && (
        <p
          id={errorId}
          role="alert"
          className="mt-3 flex items-start justify-center gap-1.5 text-[13px] text-clay text-center"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={2} />
          <span>{shownError}</span>
        </p>
      )}
    </div>
  );
}
